import axios from 'axios';

import i18n from '../i18n';

const AUTH_SESSION_STORAGE_KEYS = [
  'AuthToken',
  'AccountData',
  'AuthorizedAccounts',
  'originalAuthToken',
  'email',
  'originalEmail',
  'role',
  'originalRole',
  'originalAccountData',
  'AuthApiHost',
  'AuthDeploymentEnv',
];

function getCurrentAuthEnvironment() {
  return {
    apiHost: process.env.REACT_APP_API_HOST || '',
    deploymentEnv: process.env.REACT_APP_DEPLOYMENT_ENV || '',
  };
}

function persistAuthEnvironment() {
  const { apiHost, deploymentEnv } = getCurrentAuthEnvironment();
  localStorage.setItem('AuthApiHost', apiHost);
  localStorage.setItem('AuthDeploymentEnv', deploymentEnv);
}

function clearAuthorizationHeader() {
  if (axios.defaults.headers.common) {
    delete axios.defaults.headers.common.Authorization;
  }
}

export function clearAuthSession() {
  AUTH_SESSION_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
  });
  clearAuthorizationHeader();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth-session-cleared'));
  }
}

export function isAuthSessionForCurrentEnvironment() {
  const token = localStorage.getItem('AuthToken');
  if (!token) {
    return true;
  }

  const { apiHost, deploymentEnv } = getCurrentAuthEnvironment();
  return (
    localStorage.getItem('AuthApiHost') === apiHost &&
    localStorage.getItem('AuthDeploymentEnv') === deploymentEnv
  );
}

export function restoreAuthSessionIfValid() {
  if (!isAuthSessionForCurrentEnvironment()) {
    clearAuthSession();
    return;
  }

  const token = localStorage.getItem('AuthToken');
  if (token) {
    axios.defaults.headers.common = {
      ...axios.defaults.headers.common,
      Authorization: `Bearer ${token}`,
    };
  }
}

export function persistAuthSession(response = {}) {
  const token = response.token;
  const communication_language = response.communication_language;

  localStorage.setItem('email', response.email);
  localStorage.setItem('originalEmail', response.email);
  localStorage.setItem('AuthToken', token);
  localStorage.setItem('role', response.role);
  localStorage.setItem('originalRole', response.role);
  localStorage.setItem('AccountData', JSON.stringify(response.account));
  localStorage.setItem(
    'originalAccountData',
    JSON.stringify(response.account),
  );
  persistAuthEnvironment();

  if (communication_language) {
    i18n.changeLanguage(communication_language.toLowerCase());
  }

  axios.defaults.headers.common = {
    ...axios.defaults.headers.common,
    Authorization: `Bearer ${token}`,
  };
}

export function loginRequest(loginObject, callback, errorcallback) {
  clearAuthorizationHeader();
  axios
    .post(`auth/login`, loginObject)
    .then((res) => {
      const response = res.data;
      persistAuthSession(response);
      callback(response);
    })
    .catch((error) => {
      errorcallback(error);
    });
}

export function oauthExchangeRequest(oauthToken, callback, errorcallback) {
  clearAuthorizationHeader();
  axios
    .post(`auth/oauth_exchange`, { oauth_token: oauthToken })
    .then((res) => {
      const response = res.data;
      persistAuthSession(response);
      callback(response);
    })
    .catch((error) => {
      errorcallback(error);
    });
}

export function logoutRequest(callback, errorcallback) {
  const originalAuthToken = localStorage.getItem('originalAuthToken');

  const finishLogout = (response, isError) => {
    clearAuthSession();
    i18n.changeLanguage(); // calling without argument uses auto language detector
    if (isError) {
      errorcallback(response);
    } else {
      callback(response);
    }
  };

  axios
    .post(`auth/logout`)
    .then((res) => finishLogout(res.data, false))
    .catch((error) => finishLogout(error, true));

  if (originalAuthToken) {
    axios
      .post(`auth/logout`, null, {
        headers: { Authorization: `Bearer ${originalAuthToken}` },
      })
      .catch(() => {});
  }
}

export function resendSMS({ email, phoneNumber, captchaToken }) {
  return axios.post('users/resend_sms', {
    email,
    mobilenumber: phoneNumber,
    captchaToken,
  });
}

export function resendEmail({ accountId, email }) {
  return axios.post('users/resend_email', { account_id: accountId, email });
}

export function verifyMobile(activationObject, callback, errorcallback) {
  axios
    .post('users/activate', activationObject)
    .then((response) => {
      if (response.status == 200) {
        callback();
      }
    })
    .catch((err) => {
      errorcallback(err);
    });
}

export function verifyEmail(data, callback, errorcallback) {
  axios
    .post('users/verify_email', data)
    .then((response) => {
      if (response.status == 200) {
        callback();
      }
    })
    .catch((err) => {
      errorcallback(err);
    });
}

export async function loginToMasterAccount(
  account_id,
  callback,
  errorcallback,
) {
  axios
    .post(`auth/login_to_master_account`, { account_id: account_id })
    .then(async (response) => {
      response = response.data;
      const token = response.token;

      const originalAuthToken = localStorage.getItem('AuthToken');
      localStorage.setItem('originalAuthToken', originalAuthToken);
      localStorage.setItem('AuthToken', response.token);
      localStorage.setItem('email', response.email);
      localStorage.setItem('role', response.role);

      localStorage.setItem('AccountData', JSON.stringify(response.account));
      persistAuthEnvironment();

      axios.defaults.headers.common = {
        ...axios.defaults.headers.common,
        Authorization: `Bearer ${token}`,
      };
      localStorage.removeItem('AuthorizedAccounts');
      callback(response);
      window.location.reload();
    })
    .catch((error) => {
      errorcallback(error);
    });
}

export async function goBackToMainAccount() {
  const originalAuthToken = localStorage.getItem('originalAuthToken');
  if (originalAuthToken) {
    await axios.post(`auth/logout`);

    localStorage.setItem('AuthToken', originalAuthToken);
    localStorage.setItem('email', localStorage.getItem('originalEmail'));
    localStorage.setItem('role', localStorage.getItem('originalRole'));
    localStorage.setItem(
      'AccountData',
      localStorage.getItem('originalAccountData'),
    );

    axios.defaults.headers.common = {
      Authorization: `Bearer ${originalAuthToken}`,
    };

    localStorage.removeItem('AuthorizedAccounts');
    window.location.reload();
  }
}

export const AUTHORIZED_ACCOUNTS_UPDATED_EVENT = 'authorized-accounts-updated';

let authorizedAccountsRequest = null;

export function readAuthorizedAccounts() {
  try {
    const stored = localStorage.getItem('AuthorizedAccounts');
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function hasAuthorizedAccountsCache() {
  return localStorage.getItem('AuthorizedAccounts') !== null;
}

function rememberAuthorizedAccounts(accounts) {
  const list = Array.isArray(accounts) ? accounts : [];
  localStorage.setItem('AuthorizedAccounts', JSON.stringify(list));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(AUTHORIZED_ACCOUNTS_UPDATED_EVENT, { detail: list }),
    );
  }
  return list;
}

export function getMyAuthorizedAccounts(callback, errorcallback) {
  axios
    .get(`auth/get_my_account_authoritations`)
    .then((response) => {
      callback(response.data);
    })
    .catch((error) => {
      errorcallback(error);
    });
}

/** Fetch once after login and keep the result for the account menu. */
export function loadAuthorizedAccountsAfterLogin() {
  if (authorizedAccountsRequest) {
    return authorizedAccountsRequest;
  }

  authorizedAccountsRequest = new Promise((resolve) => {
    getMyAuthorizedAccounts(
      (accounts) => {
        authorizedAccountsRequest = null;
        resolve(rememberAuthorizedAccounts(accounts));
      },
      () => {
        authorizedAccountsRequest = null;
        resolve(null);
      },
    );
  });

  return authorizedAccountsRequest;
}

export function ensureAuthorizedAccountsLoaded() {
  if (!localStorage.getItem('AuthToken') || hasAuthorizedAccountsCache()) {
    return;
  }
  loadAuthorizedAccountsAfterLogin();
}

export function initiateAuth(payload) {
  return axios.post('auth/initiate', payload);
}

export function getOauthProfile(payload) {
  return axios.post('auth/oauth_profile', payload);
}

export function submitAuthPassword(payload) {
  return axios.post('auth/password', payload);
}

export function requestAuthEmailCode(payload) {
  return axios.post('auth/email_resend', payload);
}

export function verifyAuthEmailCode(payload) {
  return axios.post('auth/email_verify', payload);
}

export function saveAuthProfile(payload) {
  return axios.post('auth/profile', payload);
}

export function sendAuthMobileCode(payload) {
  return axios.post('auth/mobile_send', payload);
}

export function verifyAuthMobileCode(payload) {
  return axios.post('auth/mobile_verify', payload);
}

export function saveAuthBoat(payload) {
  return axios.post('auth/boat', payload);
}

export function saveAuthPayment(payload) {
  return axios.post('auth/payment', payload);
}

export function finalizeAuth(payload) {
  return axios.post('auth/complete', payload);
}

export function checkAuthEmailStatus(payload) {
  return axios.post('auth/email_check', payload);
}

export function appVersionCheck(callback, errorCallback) {
  axios
    .get('auth/version_check')
    .then((response) => {
      if (response.status === 200) {
        callback(response.data);
      }
    })
    .catch((err) => {
      errorCallback(err);
    });
}

export function isMaintenanceModeError(error) {
  const responseData = error?.response?.data;
  const status = error?.response?.status;

  return (
    status === 503 &&
    (responseData?.error_code === 8003 ||
      responseData?.language_variable === 'maintainenceMode')
  );
}

export function appMaintenanceCheck(onAvailable, onError) {
  axios
    .get('auth/maintainence_check')
    .then((response) => {
      if (response.status === 200) {
        onAvailable(response.data);
      }
    })
    .catch((err) => {
      onError(err);
    });
}
